<script>
  import { createEventDispatcher } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Tooltip } from '@sveltestrap/sveltestrap';
  import { differenceInDays, formatDistanceToNowStrict } from 'date-fns';

  import { ActivityEventIcons } from '@client/components/activity-event-icons';
  import { Icon } from '@client/components/icon';

  import { EmailCell } from '@client/pages/insights/activity/cell-formatters';
  import { IntentCell } from '@client/pages/insights/activity/cell-formatters';

  import { generateActivityStats, isBizcardMsglog } from '@client/utils/activity';

  import { ACTIVITY_EVENTS } from '@client/enums';

  import { formatDate, isDate } from '@common/utils/dates';
  import { generateUUID } from '@common/utils/strings';

  export let data = {};
  export let searchText = '';
  export let sortKey = '';

  let date = '';

  const dispatch = createEventDispatcher();
  const id = generateUUID();

  const onShowDetailsClick = () => dispatch('message-details', data);

  $: date = sortKey === 'lastactivity.utcts' ? new Date(data.lastactivity.utcts || data.utcts) : new Date(data.msgtime);
  // A bizcard visit has no send time, so `msgtime` is undefined on those rows. It is tracked
  // separately from `date` because the two disagree whenever the card is sorted by last
  // activity: the send time can be absent, or years older, while the activity is minutes old.
  $: msgDate = new Date(data.msgtime);
  $: bizcard = isBizcardMsglog(data);
  $: stats = generateActivityStats(data);
  $: verb = data?.lastactivity?.verb || data?.lastactivity?.objecttype || 'sent';
  $: latestEvent = ACTIVITY_EVENTS[verb] || ACTIVITY_EVENTS[data?.lastactivity?.objecttype] || ACTIVITY_EVENTS.sent;
  // Falling back to Infinity keeps an unparseable date out of the relative-time branch, which
  // throws on one. `!isNaN(date) && differenceInDays(...)` did the opposite: it yields `false`
  // for an invalid date, and `false < 30` is true.
  $: days = isDate(date) ? differenceInDays(new Date(), date) : Infinity;
  $: msgDays = isDate(msgDate) ? differenceInDays(new Date(), msgDate) : Infinity;
</script>

<div class="message-card" role="button" tabindex="0" on:click={onShowDetailsClick}>
  <div class="message-card-details">
    <div class="header">
      <div class="date">
        {#if !isDate(date)}
          -
        {:else if sortKey === 'lastactivity.utcts' && days < 30}
          {formatDistanceToNowStrict(date, { addSuffix: true })}
        {:else}
          {formatDate(date, 'M/d/yyyy h:mm a')}
        {/if}
      </div>
      <div class="events">
        {#if latestEvent}
          <div id="tooltip-{id}" class="badge" style="background-color: {latestEvent.color};">
            {$format(latestEvent.label)}
          </div>

          <Tooltip target="tooltip-{id}">{$format('label.TOOLTIP.THREAD_EVENT')}</Tooltip>
        {/if}
      </div>
      <div class="action">
        <Icon name="arrow-up-right" class="d-block" />
      </div>
    </div>
    <div class="message">
      {#if data.lastactivity?.recipient && latestEvent}
        <div class="last-event">
          <ActivityEventIcons bind:data showOnly={[latestEvent.id]} />
          <span>{data.lastactivity?.recipient}</span>
        </div>
      {/if}

      <div class="subject">
        <EmailCell row={data} props={{ searchText, hideIcons: true }} />
      </div>
      <div class="info">
        <div class="stats">
          <IntentCell row={data} />

          {#each Object.entries(stats) as [value, config]}
            {#if config.count}
              <div class="item" id="user-stats-tooltip-{value}-{id}">
                <i class={config.icon} />
                <span>{config.count}</span>
              </div>
              <Tooltip target="user-stats-tooltip-{value}-{id}">
                {$format(config.iconTooltip)}
              </Tooltip>
            {/if}
          {/each}
        </div>
      </div>

      <!-- A business card was never sent, so it has no original send time to report. -->
      {#if sortKey === 'lastactivity.utcts' && !bizcard}
        <div class="original">
          <span>{$format('label.ORIGINALLY_SENT')}</span>
          <span>
            {#if !isDate(msgDate)}
              -
            {:else if msgDays < 30}
              {formatDistanceToNowStrict(msgDate, { addSuffix: true })}
            {:else}
              {formatDate(msgDate, 'M/d/yyyy h:mm a')}
            {/if}
          </span>
        </div>
      {/if}
    </div>
  </div>
</div>

<style lang="scss" src="./message-card.scss"></style>
